import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigationType } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AdCard from "../components/AdCard";
import SEOHead from "../components/SEOHead";
import { CategorySchema, FAQSchema, LocalBusinessSchema, SiteNavigationSchema } from "../components/SchemaMarkup";
import AEOContent, { CATEGORY_AEO_QUESTIONS } from "../components/AEOContent";
import Breadcrumbs from "../components/Breadcrumbs";
import AgeVerificationModal from "../components/AgeVerificationModal";
import CityQuickFilter from "../components/CityQuickFilter";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  MapPin,
  Layers,
  Tag,
  ArrowDownNarrowWide,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SEO keywords for each category
const CATEGORY_SEO = {
  auto: {
    title: "Mașini Second Hand România - Auto SH, Autoturisme de Vânzare | X67",
    description: "Anunțuri mașini second hand din toată România. BMW, Audi, Mercedes, VW, Ford și alte mărci. Prețuri de la 1000€. Găsește mașina perfectă!",
    keywords: "masini second hand, auto sh, autoturisme, masini ieftine, vanzari auto, bmw sh, audi second, mercedes ocasii, vw golf, ford focus"
  },
  imobiliare: {
    title: "Apartamente și Case de Vânzare România - Imobiliare | X67",
    description: "Anunțuri imobiliare din toată România. Apartamente, case, terenuri, spații comerciale. Prețuri actualizate zilnic!",
    keywords: "apartamente vanzare, case vanzare, imobiliare romania, terenuri, garsoniere, apartamente 2 camere, case ieftine, chirii"
  },
  electronice: {
    title: "Electronice Second Hand - Telefoane, Laptopuri, TV | X67",
    description: "Electronice și electrocasnice second hand. iPhone, Samsung, laptopuri, televizoare la prețuri mici!",
    keywords: "electronice second hand, telefoane ieftine, iphone sh, samsung second, laptopuri, televizoare, electrocasnice"
  },
  locuri_munca: {
    title: "Locuri de Muncă România - Joburi, Angajări 2024 | X67",
    description: "Cele mai noi locuri de muncă din România. Joburi full-time, part-time, remote. Aplică acum!",
    keywords: "locuri de munca, joburi romania, angajari, cariera, oferte munca, job remote, part time, full time"
  },
  moda: {
    title: "Haine și Încălțăminte - Modă Second Hand | X67",
    description: "Haine de firmă second hand, încălțăminte, accesorii. Branduri originale la prețuri mici!",
    keywords: "haine second hand, incaltaminte, moda, accesorii, branduri, rochii, pantofi, genti"
  },
  servicii: {
    title: "Servicii România - Reparații, Transport, Curățenie | X67",
    description: "Servicii profesionale în toată România. Reparații, transport, curățenie, instalații și multe altele!",
    keywords: "servicii romania, reparatii, transport, curatenie, instalatii, zugraveli, meditații"
  },
  animale: {
    title: "Animale de Companie - Câini, Pisici, Accesorii | X67",
    description: "Anunțuri animale de companie. Câini, pisici, păsări, accesorii pentru animale!",
    keywords: "animale companie, caini, pisici, pasari, accesorii animale, hrana animale, pet shop"
  },
  escorts: {
    title: "Escorte România - Companie pentru Evenimente | X67",
    description: "Servicii de escortă și companie pentru evenimente în România.",
    keywords: "escorte, companie, evenimente, intalniri"
  }
};

// SEO-friendly slug → category id (bidirectional with backend cat_slugs)
const SLUG_TO_ID = {
  "auto": "cars",
  "imobiliare": "real_estate",
  "electronice": "electronics",
  "locuri-de-munca": "jobs",
  "locuri_munca": "jobs",
  "moda": "fashion",
  "servicii": "services",
  "animale": "animals",
  "escorte": "escorts",
  "colectionabile": "collectibles",
  "mobila": "furniture",
  "sport": "sports_hobby",
  "constructii": "construction",
  "mama-copil": "kids_baby",
};

// Category accent gradients keyed by backend category ID (premium hero halo)
const CATEGORY_GRADIENTS = {
  cars: { from: "from-blue-500/30", via: "via-cyan-500/20", to: "to-transparent", chip: "from-blue-600 to-cyan-600", text: "text-blue-300" },
  real_estate: { from: "from-emerald-500/30", via: "via-teal-500/20", to: "to-transparent", chip: "from-emerald-600 to-teal-600", text: "text-emerald-300" },
  electronics: { from: "from-violet-500/30", via: "via-purple-500/20", to: "to-transparent", chip: "from-violet-600 to-purple-600", text: "text-violet-300" },
  jobs: { from: "from-amber-500/30", via: "via-orange-500/20", to: "to-transparent", chip: "from-amber-600 to-orange-600", text: "text-amber-300" },
  fashion: { from: "from-pink-500/30", via: "via-rose-500/20", to: "to-transparent", chip: "from-pink-600 to-rose-600", text: "text-pink-300" },
  services: { from: "from-cyan-500/30", via: "via-sky-500/20", to: "to-transparent", chip: "from-cyan-600 to-sky-600", text: "text-cyan-300" },
  animals: { from: "from-lime-500/30", via: "via-green-500/20", to: "to-transparent", chip: "from-lime-600 to-green-600", text: "text-lime-300" },
  escorts: { from: "from-fuchsia-500/30", via: "via-pink-500/20", to: "to-transparent", chip: "from-fuchsia-600 to-pink-600", text: "text-fuchsia-300" },
  collectibles: { from: "from-amber-500/30", via: "via-yellow-500/20", to: "to-transparent", chip: "from-amber-600 to-yellow-600", text: "text-amber-300" },
  furniture: { from: "from-orange-500/30", via: "via-amber-500/20", to: "to-transparent", chip: "from-orange-600 to-amber-600", text: "text-orange-300" },
  sports_hobby: { from: "from-emerald-500/30", via: "via-lime-500/20", to: "to-transparent", chip: "from-emerald-600 to-lime-600", text: "text-emerald-300" },
  construction: { from: "from-stone-500/30", via: "via-orange-500/20", to: "to-transparent", chip: "from-stone-600 to-orange-600", text: "text-stone-300" },
  kids_baby: { from: "from-pink-500/30", via: "via-fuchsia-500/20", to: "to-transparent", chip: "from-pink-600 to-fuchsia-600", text: "text-pink-300" },
  default: { from: "from-blue-500/30", via: "via-fuchsia-500/20", to: "to-transparent", chip: "from-blue-600 to-fuchsia-600", text: "text-slate-300" },
};

export default function CategoryPage() {
  const { categoryId: rawCategoryId } = useParams();
  // Resolve Romanian slug (auto, imobiliare) to backend ID (cars, real_estate)
  const categoryId = SLUG_TO_ID[rawCategoryId] || rawCategoryId;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigationType = useNavigationType();

  const [ads, setAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAds, setTotalAds] = useState(0);

  // Scroll restoration
  useEffect(() => {
    if (navigationType === "POP") {
      const saved = sessionStorage.getItem(`scroll_category_${categoryId}`);
      if (saved) {
        setTimeout(() => window.scrollTo(0, parseInt(saved, 10)), 100);
      }
    }
    const handleScroll = () => {
      sessionStorage.setItem(`scroll_category_${categoryId}`, window.scrollY.toString());
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [categoryId, navigationType]);

  // Age verification for escorts
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);

  useEffect(() => {
    if (categoryId === "escorts" || categoryId === "escorte") {
      const isVerified = localStorage.getItem("age_verified_18plus") === "true";
      if (!isVerified) {
        setShowAgeModal(true);
        setAgeVerified(false);
      } else {
        setAgeVerified(true);
      }
    } else {
      setAgeVerified(true);
    }
  }, [categoryId]);

  // Filters
  const [selectedSubcategory, setSelectedSubcategory] = useState("all");
  const [selectedCity, setSelectedCity] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const currentCategory = categories.find((c) => c.id === categoryId);
  const grad = CATEGORY_GRADIENTS[categoryId] || CATEGORY_GRADIENTS.default;

  // SEO data lookup — try Romanian slug first, then English id
  const seoData = CATEGORY_SEO[rawCategoryId] || CATEGORY_SEO[categoryId] || {
    title: `Anunțuri ${currentCategory?.name || categoryId} România | X67 Digital`,
    description: `Cele mai bune anunțuri ${currentCategory?.name || categoryId} din România. Prețuri actualizate live!`,
    keywords: `${currentCategory?.name || categoryId}, anunturi, romania`
  };

  // Helper function to change page and scroll to top
  const handlePageChange = (newPage) => {
    if (typeof newPage === "function") {
      setPage((prev) => {
        const nextPage = newPage(prev);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return nextPage;
      });
    } else {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, selectedSubcategory, selectedCity, sortBy, minPrice, maxPrice, page]);

  const fetchInitialData = async () => {
    try {
      const [categoriesRes, citiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/cities`),
      ]);
      setCategories(categoriesRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchAds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryId && categoryId !== "all") {
        params.append("category_id", categoryId);
      }
      if (selectedSubcategory && selectedSubcategory !== "all") params.append("subcategory_id", selectedSubcategory);
      if (selectedCity && selectedCity !== "all") params.append("city_id", selectedCity);
      if (sortBy) params.append("sort", sortBy);
      if (minPrice) params.append("min_price", minPrice);
      if (maxPrice) params.append("max_price", maxPrice);
      if (searchQuery) params.append("search", searchQuery);
      params.append("page", page);
      params.append("limit", 20);

      const response = await axios.get(`${API_URL}/api/ads?${params.toString()}`);
      setAds(response.data.ads || []);
      setTotalPages(response.data.pages || 1);
      setTotalAds(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching ads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAds();
  };

  const clearFilters = () => {
    setSelectedSubcategory("all");
    setSelectedCity("all");
    setSortBy("newest");
    setMinPrice("");
    setMaxPrice("");
    setSearchQuery("");
    setPage(1);
  };

  const hasActiveFilters =
    (selectedSubcategory && selectedSubcategory !== "all") ||
    (selectedCity && selectedCity !== "all") ||
    minPrice ||
    maxPrice ||
    searchQuery;

  const subcatLabel = currentCategory?.subcategories?.find((s) => s.id === selectedSubcategory)?.name;
  const cityLabel = cities.find((c) => c.id === selectedCity)?.name;

  // Active filter chips
  const activeChips = [];
  if (subcatLabel) activeChips.push({ key: "subcat", label: subcatLabel, icon: Layers, onClear: () => setSelectedSubcategory("all") });
  if (cityLabel) activeChips.push({ key: "city", label: cityLabel, icon: MapPin, onClear: () => setSelectedCity("all") });
  if (minPrice) activeChips.push({ key: "min", label: `≥ ${minPrice} €`, icon: Tag, onClear: () => setMinPrice("") });
  if (maxPrice) activeChips.push({ key: "max", label: `≤ ${maxPrice} €`, icon: Tag, onClear: () => setMaxPrice("") });
  if (searchQuery) activeChips.push({ key: "q", label: `"${searchQuery}"`, icon: Search, onClear: () => setSearchQuery("") });

  const handleAgeConfirm = () => {
    setShowAgeModal(false);
    setAgeVerified(true);
  };
  const handleAgeCancel = () => setShowAgeModal(false);

  const sortLabel = {
    newest: "Cele mai noi",
    oldest: "Cele mai vechi",
    price_low: "Preț ↑",
    price_high: "Preț ↓",
    boosted: "Ridicate",
  }[sortBy] || "Sortează";

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="category-page">
      <AgeVerificationModal isOpen={showAgeModal} onConfirm={handleAgeConfirm} onCancel={handleAgeCancel} />

      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={`https://x67digital.com/category/${categoryId}`}
      />
      <CategorySchema category={currentCategory} ads={ads} page={page} totalAds={totalAds} />
      <SiteNavigationSchema />
      <LocalBusinessSchema city={cityLabel || "România"} />
      {currentCategory && (
        <FAQSchema
          faqs={CATEGORY_AEO_QUESTIONS(currentCategory.name, cityLabel).map(q => ({
            question: q.question,
            answer: q.answer.replace(/<[^>]+>/g, "")
          }))}
        />
      )}
      <Header />

      {/* Premium Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 -z-10">
          <div className={`absolute -top-32 -left-20 w-[500px] h-[500px] rounded-full bg-gradient-to-br ${grad.from} ${grad.via} ${grad.to} blur-[110px]`} />
          <div className={`absolute -top-20 right-0 w-[400px] h-[400px] rounded-full bg-gradient-to-tl ${grad.from} ${grad.via} ${grad.to} blur-[120px] opacity-70`} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">
          <Breadcrumbs items={[{ label: currentCategory?.name || "Toate Anunțurile" }]} className="mb-4" />

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-semibold ${grad.text} mb-3`}>
                <Sparkles className="w-3 h-3" />
                {currentCategory?.subcategories?.length || 0} subcategorii · actualizat live
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05] mb-2">
                {currentCategory?.name || "Toate anunțurile"}
              </h1>
              <p className="text-slate-400 text-sm sm:text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="tabular-nums font-bold text-white">{totalAds.toLocaleString("ro-RO")}</span>
                <span>anunțuri găsite</span>
                {hasActiveFilters && <span className="text-slate-500">· filtre active</span>}
              </p>
            </div>

            {/* Quick search + sort + mobile filter trigger */}
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <form onSubmit={handleSearch} className="relative flex-1 sm:min-w-[280px]">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Caută în categorie…"
                  className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 focus:border-blue-500/50 focus:outline-none text-white placeholder:text-slate-500 text-sm"
                  data-testid="hero-search-cat"
                />
              </form>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-11 w-full sm:w-44 bg-white/5 backdrop-blur-md border-white/10 text-white hover:bg-white/10" data-testid="hero-sort">
                  <ArrowDownNarrowWide className="w-4 h-4 text-slate-400 mr-2" />
                  <SelectValue placeholder="Sortează">{sortLabel}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  <SelectItem value="newest">Cele mai noi</SelectItem>
                  <SelectItem value="oldest">Cele mai vechi</SelectItem>
                  <SelectItem value="price_low">Preț: mic-mare</SelectItem>
                  <SelectItem value="price_high">Preț: mare-mic</SelectItem>
                  {categoryId === "escorts" && <SelectItem value="boosted">Ridicate</SelectItem>}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                className="lg:hidden h-11 border-white/10 text-white hover:bg-white/5"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="filter-toggle"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtre
                {hasActiveFilters && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold rounded-full bg-emerald-500 text-black">
                    {activeChips.length}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Active filter chips */}
          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5">
              {activeChips.map((c) => (
                <button
                  key={c.key}
                  onClick={c.onClear}
                  className="group inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 text-xs text-slate-200 transition-colors"
                >
                  <c.icon className="w-3.5 h-3.5 text-slate-400" />
                  {c.label}
                  <X className="w-3.5 h-3.5 text-slate-500 group-hover:text-rose-400 transition-colors" />
                </button>
              ))}
              <button
                onClick={clearFilters}
                className="text-xs px-3 py-1.5 rounded-full text-slate-400 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
              >
                Șterge tot
              </button>
            </div>
          )}
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-28 lg:pb-12">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <aside className={`${showFilters ? "fixed inset-0 z-50 bg-[#050505]/95 backdrop-blur-xl p-4 overflow-y-auto" : "hidden"} lg:block lg:relative lg:w-72 lg:flex-shrink-0`}>
            <div className="lg:sticky lg:top-24">
              <div className="rounded-2xl bg-gradient-to-b from-white/[0.04] to-white/[0.02] border border-white/10 backdrop-blur-md p-5 space-y-5 shadow-xl shadow-black/40">
                {/* Mobile header */}
                <div className="flex items-center justify-between lg:hidden">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <SlidersHorizontal className="w-5 h-5 text-blue-400" />
                    Filtre
                  </h2>
                  <Button variant="ghost" size="icon" onClick={() => setShowFilters(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <div className="hidden lg:flex items-center gap-2 pb-3 border-b border-white/5">
                  <SlidersHorizontal className="w-4 h-4 text-blue-400" />
                  <h2 className="text-sm font-bold text-white uppercase tracking-wide">Filtre</h2>
                  {hasActiveFilters && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold rounded-full bg-emerald-500 text-black">
                      {activeChips.length}
                    </span>
                  )}
                </div>

                {/* Search */}
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Caută</label>
                  <form onSubmit={handleSearch} className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Caută anunțuri…"
                      className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-slate-600"
                      data-testid="filter-search"
                    />
                  </form>
                </div>

                {/* Subcategory */}
                {currentCategory?.subcategories && currentCategory.subcategories.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Subcategorie</label>
                    <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                      <SelectTrigger className="bg-[#121212] border-white/10 text-white" data-testid="filter-subcategory">
                        <SelectValue placeholder="Toate subcategoriile" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="all">Toate</SelectItem>
                        {currentCategory.subcategories.map((sub) => (
                          <SelectItem key={sub.id} value={sub.id}>
                            {sub.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* City */}
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Oraș</label>
                  <CityQuickFilter cities={cities} value={selectedCity} onChange={setSelectedCity} />
                </div>

                {/* Price Range */}
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Preț</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      placeholder="Min"
                      className="bg-[#121212] border-white/10 text-white placeholder:text-slate-600"
                      data-testid="filter-min-price"
                    />
                    <Input
                      type="number"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      placeholder="Max"
                      className="bg-[#121212] border-white/10 text-white placeholder:text-slate-600"
                      data-testid="filter-max-price"
                    />
                  </div>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    className="w-full border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                    onClick={clearFilters}
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Șterge filtrele
                  </Button>
                )}

                {/* Apply Filters - Mobile */}
                <Button
                  className="w-full lg:hidden bg-gradient-to-r from-blue-600 to-fuchsia-600 hover:from-blue-500 hover:to-fuchsia-500 text-white font-bold shadow-lg shadow-blue-500/30"
                  onClick={() => {
                    setShowFilters(false);
                    fetchAds();
                  }}
                >
                  Aplică filtrele
                </Button>
              </div>
            </div>
          </aside>

          {/* Ads Grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="rounded-2xl bg-[#0A0A0A] aspect-[4/3] animate-pulse" />
                ))}
              </div>
            ) : ads.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                  {ads.map((ad) => (
                    <AdCard key={ad.ad_id} ad={ad} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={page === 1}
                      onClick={() => handlePageChange((p) => p - 1)}
                      className="border-white/10 text-white hover:bg-white/5 disabled:opacity-40"
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>

                    <div className="flex items-center gap-1">
                      {(() => {
                        const max = Math.min(5, totalPages);
                        const start = Math.max(1, Math.min(totalPages - max + 1, page - 2));
                        return [...Array(max)].map((_, i) => {
                          const pageNum = start + i;
                          return (
                            <Button
                              key={pageNum}
                              variant={page === pageNum ? "default" : "outline"}
                              size="icon"
                              onClick={() => handlePageChange(pageNum)}
                              className={
                                page === pageNum
                                  ? "bg-gradient-to-br from-blue-600 to-fuchsia-600 text-white border-0 shadow-lg shadow-blue-500/30"
                                  : "border-white/10 text-white hover:bg-white/5"
                              }
                            >
                              {pageNum}
                            </Button>
                          );
                        });
                      })()}
                    </div>

                    <Button
                      variant="outline"
                      size="icon"
                      disabled={page === totalPages}
                      onClick={() => handlePageChange((p) => p + 1)}
                      className="border-white/10 text-white hover:bg-white/5 disabled:opacity-40"
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 rounded-2xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-fuchsia-500/20 border border-white/10 mb-4">
                  <Search className="w-7 h-7 text-slate-300" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">Niciun anunț găsit</h3>
                <p className="text-slate-400 mb-6 max-w-md mx-auto px-6 text-sm">
                  Încearcă să schimbi filtrele sau {hasActiveFilters && "să le ștergi pe toate."}
                </p>
                <div className="flex gap-3 justify-center">
                  {hasActiveFilters && (
                    <Button variant="outline" className="border-white/10 text-white hover:bg-white/5" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-2" />
                      Șterge filtrele
                    </Button>
                  )}
                  <Link to="/create-ad">
                    <Button className="bg-gradient-to-r from-blue-600 to-fuchsia-600 hover:from-blue-500 hover:to-fuchsia-500 text-white font-bold shadow-lg shadow-blue-500/30">
                      Adaugă primul anunț
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Premium AEO Content + SEO long-form intro */}
        {currentCategory && (
          <section className="mt-16 pt-12 border-t border-white/5">
            {/* SEO long-form content (visible to crawlers + users) */}
            <div className="max-w-4xl mx-auto mb-12 prose prose-invert prose-sm sm:prose-base">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2" data-speakable="true">
                <span className="inline-block w-1 h-6 rounded-full bg-gradient-to-b from-emerald-500 to-cyan-500" />
                Anunțuri {currentCategory.name}{cityLabel ? ` în ${cityLabel}` : " în România"} pe X67 Digital
              </h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Categoria <strong className="text-white">{currentCategory.name}</strong> de pe X67 Digital include{" "}
                <span className="tabular-nums font-bold text-emerald-300">{totalAds.toLocaleString("ro-RO")}</span> anunțuri active{cityLabel ? ` în ${cityLabel}` : " în toată România"}, actualizate live. Vânzătorii sunt verificați KYC prin AI face-match (Gemini Vision), iar tranzacțiile sunt protejate de <a href="/escrow" className="text-emerald-300 hover:text-white transition-colors">sistemul Escrow</a> cu Viva Wallet (banii blocați până la livrare confirmată).
              </p>
              {currentCategory.subcategories && currentCategory.subcategories.length > 0 && (
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed mt-3">
                  Explorează cele <strong>{currentCategory.subcategories.length} subcategorii</strong>:{" "}
                  {currentCategory.subcategories.slice(0, 8).map((s, idx) => (
                    <span key={s.id}>
                      <button
                        onClick={() => setSelectedSubcategory(s.id)}
                        className="text-emerald-300 hover:text-white transition-colors underline-offset-2 hover:underline"
                      >
                        {s.name}
                      </button>
                      {idx < Math.min(currentCategory.subcategories.length, 8) - 1 ? ", " : ""}
                    </span>
                  ))}
                  {currentCategory.subcategories.length > 8 && " și altele"}.
                </p>
              )}
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mt-3">
                Folosește <strong>Smart Location Picker</strong> pentru a căuta anunțuri din orașul tău (369 localități indexate, suport complet pentru diacritice românești). Salvează căutarea pentru a primi <strong>alerte email instant</strong> când apar anunțuri noi care îndeplinesc criteriile tale. Compară până la 4 anunțuri side-by-side cu butonul <strong>Compară</strong>, fă oferte de preț directe sau folosește <strong>chat-ul WebSocket</strong> pentru a contacta vânzătorul.
              </p>
            </div>

            <AEOContent
              title={`Întrebări frecvente — ${currentCategory.name}${cityLabel ? ` ${cityLabel}` : ""}`}
              subtitle="Răspunsuri rapide optimizate pentru asistenți AI și voice search."
              questions={CATEGORY_AEO_QUESTIONS(currentCategory.name, cityLabel)}
            />
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
